import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Stack,
} from "@mui/material";
import { Close, WarningAmberRounded } from "@mui/icons-material";
import { quickbooksService } from "../../../api/quickbooks";

const GL_TASK_KEYS = [
  "general_ledger",
  "general_ledger_cdc",
  "general_ledger_build",
  "general_ledger_fetch",
];

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = String(hours % 12 || 12).padStart(2, "0");
  return `${month}/${day}/${year}, ${hour12}:${minutes}:${seconds} ${ampm}`;
};

const normalizeErrors = (errors) => {
  if (!errors) return [];
  if (Array.isArray(errors)) {
    return errors.map((err) => {
      if (err && typeof err === "object") {
        const title = err.error || "Error";
        const detail = err.detail;
        const details = Array.isArray(detail)
          ? detail.map((d) => String(d))
          : detail
          ? [String(detail)]
          : [];
        return { title: String(title), details };
      }
      return { title: String(err), details: [] };
    });
  }
  if (typeof errors === "string") {
    return errors
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => ({ title: line, details: [] }));
  }
  return [{ title: "Unknown error", details: [] }];
};

const extractTaskErrors = (taskResult) => {
  if (!taskResult) return [];
  return normalizeErrors(taskResult.errors);
};

const ErrorCard = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No errors found.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {items.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            display: "flex",
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            backgroundColor: "#fdecec",
          }}
        >
          <WarningAmberRounded sx={{ color: "#b91c1c", mt: 0.2 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#7f1d1d" }}>
              {item.title}
            </Typography>
            {item.details && item.details.length > 0 && (
              <Stack component="ul" sx={{ m: 0, mt: 0.75, pl: 2 }} spacing={0.5}>
                {item.details.map((detail, i) => (
                  <Typography key={i} component="li" variant="body2" sx={{ color: "#7f1d1d" }}>
                    {detail}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

const QBErrorDetailsDialog = ({ open, onClose, jobId, customerId, accountName }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchJobDetails = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const response = await quickbooksService.getJobDetails(jobId);
      setJob(response.data);
    } catch (error) {
      console.error("Error fetching job details:", error);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (open && jobId) {
      fetchJobDetails();
    }
  }, [open, jobId, fetchJobDetails]);

  const jobResults = job?.results || {};
  const targetCustomerId = customerId || Object.keys(jobResults)[0];
  const customerResults = targetCustomerId ? jobResults[targetCustomerId] : null;

  const headerAccountName = useMemo(() => {
    if (accountName) return accountName;
    const name =
      customerResults?.customerDetails?.name ||
      job?.details?.customer_name ||
      job?.details?.account_name;
    if (name) return name;
    if (targetCustomerId) return `Account ${String(targetCustomerId).slice(0, 8)}`;
    return "Account";
  }, [accountName, customerResults, job?.details, targetCustomerId]);

  const glErrors = useMemo(() => {
    if (!customerResults) return [];
    const collected = [];
    GL_TASK_KEYS.forEach((key) => {
      collected.push(...extractTaskErrors(customerResults[key]));
    });
    return collected;
  }, [customerResults]);

  const tbErrors = useMemo(() => {
    if (!customerResults) return [];
    return extractTaskErrors(customerResults.trial_balance);
  }, [customerResults]);

  const timestamp = job?.completed_at || job?.started_at;

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      keepMounted
      sx={{
        zIndex: 200001,
      }}
      slotProps={{
        backdrop: { sx: { zIndex: 200000 } },
      }}
      container={typeof document !== 'undefined' ? document.body : undefined}
      PaperProps={{
        sx: {
          borderRadius: 3,
          zIndex: 200001,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {`Error Detail - ${headerAccountName}`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {loading ? "Loading..." : formatDateTime(timestamp)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              General Ledger
            </Typography>
            <ErrorCard items={glErrors} />
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Trial Balance
            </Typography>
            <ErrorCard items={tbErrors} />
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default QBErrorDetailsDialog;
